import { useQuery } from '@tanstack/react-query'
import { invokeEdgeOrThrow } from '@/lib/edge-functions'
import { useOrg } from '@/contexts/OrgContext'

// Rich prompt summary resolved by cadence-flow-metrics from ai_prompts (message
// prompt or the research prompt injected as {{research}}). Mirrors StepOut.prompt
// in supabase/functions/cadence-flow-metrics/index.ts — one shared contract so the
// consumers (StepsSection / SummarySection / PromptDrawer) never re-declare it.
export type PromptSummary = {
  id: string
  name: string | null
  prompt_type: string | null
  tone: string | null
  language: string | null
  objective: string | null
  body_length: number
  has_variables: boolean
}

// Per-step "is this touch wired?" signal computed by the edge fn (cadence-scoped
// with org-global NULL fallback, matching the runtime cascade).
//
// `rubrica_heredada` / `angle_heredado` expose the PROVENANCE of the winning
// effective row (carril 1): true = the effective row is org-global (cadence_id
// IS NULL), inherited from merchant-Yuno doctrine (the 9-day); false = this
// cadence's own row. They are OPTIONAL here because older/incomplete payloads may
// omit them — consumers MUST guard with `=== true` and never assume presence.
// SummarySection uses them so it never paints "listo/verde" when a check resolves
// only because it inherited org-global policy rather than owning its own.
export type StepCompleteness = {
  tiene_prompt: boolean
  tiene_rubrica: boolean
  tiene_angle: boolean
  rubrica_heredada?: boolean
  angle_heredado?: boolean
}

export type FlowStep = {
  step_id: string
  day_offset: number
  order_in_day: number
  step_type: string
  step_label: string
  skill: { id: string; name: string; display_name: string } | null
  signal_allocation: string | null
  config: {
    has_ai_prompt: boolean
    has_research_prompt: boolean
    has_template: boolean
  }
  // Enrichment (additive): the edge fn always emits these, but they are optional
  // here so consumers keep guarding with `?.` for forward/backward compatibility.
  prompt?: PromptSummary | null
  research_prompt?: PromptSummary | null
  completeness?: StepCompleteness
  carlos: {
    threshold: number | null
    min_acceptable: number | null
    max_attempts: number | null
    avg_score_30d: number | null
    samples: number
    // [D] The step-specific rubric that Carlos applies when judging THIS touch
    // (e.g. the Day-9 ban on generic intros). Exposed read-only by the edge fn
    // (carril 4) so it can be shown as a transparency surface without opening
    // the editable Política textarea. Optional: absent/old payloads → null.
    system_prompt_addendum?: string | null
  }
  metrics: {
    scheduled: number
    executed: number
    failed: number
    skipped: number
    success_rate: number | null
  }
  recent_runs: Array<{
    instance_id: string
    lead_id: string
    lead_name: string
    company: string | null
    status: string
    updated_at: string
    carlos_score: number | null
  }>
}

export type CadenceFlowMetrics = {
  cadence: {
    id: string
    name: string
    status: string
    automation_mode: string
    timezone: string
    total_steps: number
    total_days: number
  }
  days_window: number
  steps: FlowStep[]
  generated_at: string
}

export function useCadenceFlowMetrics(cadenceId: string | null | undefined, daysWindow = 30) {
  const { orgId } = useOrg()

  return useQuery<CadenceFlowMetrics>({
    queryKey: ['cadence-flow-metrics', orgId, cadenceId, daysWindow],
    enabled: !!orgId && !!cadenceId,
    staleTime: 60_000,
    queryFn: () =>
      invokeEdgeOrThrow<CadenceFlowMetrics>('cadence-flow-metrics', {
        cadence_id: cadenceId,
        days_window: daysWindow,
      }),
  })
}
